import React from 'react';
import '../../styles.scss';
import { configData } from "../../config.js";
import { fetchSigned, checkForCompletion } from '../../shared/Utilities';

import {
  OwcTypography, OwcButton, OwcSwitch,
  OwcInput, OwcExpandable
} from '@one/react';
import DelayedTooltip from "../../components/general/DelayedTooltip.js";

import { partialParse } from "../../shared/partial-json-parser.js";

/**
 * @copyright Roche 2025
 * @author Nick Draper
 */

/**
 * Generic Data Product Query component
 */
class GenericDataProduct extends React.Component {

  REQUEST_IN_PROGRESS = "Waiting for a response ...";
  MAX_CONTENT_LENGTH = 40000; // If the data product is larger than this then only display the start of it

  constructor(props) {
    super(props);
    // get a reference that will be used to scroll to the response section
    this.responseRef = React.createRef();
    this.cancelRequested = React.createRef();
    this.cancelRequested.current = false;
    const selectedDataProduct = this.props.selectedDataProduct;
    this.state = {
      loadingDataProducts: true,
      selectedDataProduct: selectedDataProduct,
      groups: props.groups,
      dataProducts: {},
      url: this.props.baseUrl,
      params: null,
      responseText: null,
      responseDict: {},
      responseType: null,
      indentResponse: true,
      howToExpanded: false,
      showAbout: false,
      intendedUseExpanded: false,
      dataDictionaryExpanded: false,
      descriptionExpanded: false,
      waiting: true,
      label: {},
      partialResponse: false,
      requestStatus: "",
      resultUrl: "",
    }
  }

  scrollToResponse = () => this.responseRef.current.scrollIntoView({behavior:"smooth"});

  async componentDidMount() {
    this.loadDataProductLabel();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedDataProduct !== this.props.selectedDataProduct) {
      this.cancelRequested.current = true;
      this.setState({
          selectedDataProduct: this.props.selectedDataProduct,
          url: this.props.baseUrl,
          responseText: null,
          responseDict: {},
          responseType: "unknown",
          requestStatus: "",
          resultUrl: "",
        },
        () => this.loadDataProductLabel()
      );
    }
  }

  /**
   * Updates the state of the url
   * @param {*} value the url value
   */
  handleInputChange(value) {
    if ((value !== undefined) && (value !== null)) {
      value = value.trim()
      if (value !== "") {
        // the first charcter should be ? add it if it is not present
        if (value[0] !== "?") {
          value = "?" + value;
        }
      }
    }
    this.setState({ params: value });
  }

  /**
   * An internal method to allow instanteous downloads
   * @param {*} blob the data to download
   * @param {*} filename the filename to use
   */
  saveFile = async (blob, filename) => {
    const a = document.createElement('a');
    a.download = filename;
    a.href = URL.createObjectURL(blob);
    a.addEventListener('click', (e) => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
  };

  /**
   * Download a file of the response
   */
  downloadFile() {
    let extension = ".txt";
    let contentType = "text/plain";
    if (this.state.responseType === "xml") {
      extension = ".xml";
      contentType = "text/xml";
    } else if (this.state.responseType === "json") {
      extension = ".json";
      contentType = "application/json";
    }
    const fileName = this.state.selectedDataProduct.replaceAll(" ","-") + extension;
    if (this.state.partialResponse === false) {
      const blob = new Blob([this.getResponseText()], { type: contentType });
      this.saveFile(blob, fileName);
    } else {
      window.location.replace(this.state.resultUrl);
    }
  }

  /**
   * Indents XML
   * @param {*} xml the xml to indent
   * @param {*} tab the optional indentation value, default is tab (\t) 
   * @returns 
   */
  formatXml(xml, tab) {
    var formatted = '', indent = '';
    tab = tab || '\t';
    xml.split(/>\s*</).forEach(function (node) {
      if (node.match(/^\/\w/)) indent = indent.substring(tab.length); // decrease indent by one 'tab'
      formatted += indent + '<' + node + '>\r\n';
      if (node.match(/^<?\w[^>]*[^/]$/)) indent += tab;              // increase indent
    });
    return formatted.substring(1, formatted.length - 3);
  }

  /**
   * submits the url when the enter key is pressed
   * @param {*} ev The keypress event
   */
  handlekeyDown(ev) {
    if (ev.keyCode === 13) {
      this.handleSubmitClick();
    }
  }

  /**
   * Signs and sumbits the request and populates the response
   */
  handleSubmitClick() {
    this.cancelRequested.current = false;
    this.setState({
      responseText: this.REQUEST_IN_PROGRESS,
      responseDict: {},
      responseType: "unknown",
      partialResponse: false,
      requestStatus: "Requesting Data Product",
      resultUrl: "",
    });

    let urlToFetch = this.state.url
    if ((this.state.params !== null) && (this.state.params !== undefined)) {
      urlToFetch += this.state.params;
    }

    if ((this.props.userName !== null) && (this.props.userName !== undefined)) {
      // Add user data for DP tracking
      urlToFetch += ([null, undefined, ""].includes(this.state.params) ? "?" : "&");
      urlToFetch += `userName=${this.props.userName}`;
    }

    fetchSigned(urlToFetch)
      .then(async response => {
        let responseDict = {};
        if (response === undefined || !response.ok) {
          this.setState({
            responseText: "There was an error requesting the data product"
          }, () => {
              if(this.responseRef.current){
                this.scrollToResponse();
              }
            }
          );
        }
        else {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            responseDict = await response.json();
            if (this.cancelRequested.current === false) {
              this.handleDataProductWait(responseDict);
            }
          } else {
            this.setState({
              responseText: `The request to generate the data product failed with the message '${await response.text()}'`,
            }, () => {
                if(this.responseRef.current){
                  this.scrollToResponse();
                }
              }
            );
          }
        }   
      })
      .catch(error => {
        this.setState({ responseText: error.toString() });
        console.error('There was an error!', error);
      });
  }

  async handleDataProductWait(responseDict) {
    this.setState({requestStatus: "Generating Data Product"});
    const requestResult = await checkForCompletion(
      `${this.state.url}status/${responseDict}`, this.cancelRequested);

    if (requestResult.status === "SUCCEEDED") {
      this.setState(
        {"resultUrl": requestResult.resultUrl}, 
        ()=>this.displayResults(requestResult.resultUrl));
    } else {
      this.setState({
          responseText: requestResult.responseText,
          requestStatus: requestResult.requestStatus,
        }, 
        () => {
          if(this.responseRef.current){
            this.scrollToResponse();
          }
        }
      );
    }
  }

  /**
   * Loads the data product and displays it
   * @param {*} resultsURL 
   */
  displayResults(resultsURL){
    this.setState({requestStatus: "Reading Data Product"});
    fetch(resultsURL,{
      method: "GET",
      headers: {'Accept': '*/*'},
      mode: "cors",
      cache: "no-cache",
      referrer: "client",
    })
    .then(async response => {
      const contentLength = response.headers.get('Content-Length');
      console.log(`Content_length = ${contentLength}`)
      
      if (contentLength > this.MAX_CONTENT_LENGTH) {
        this.displayPartialResults(response);
      } else {
        let responseDict = {};
        let responseText = "";
        let responseType = "unknown";

        if (response === undefined || !response.ok) {
          responseText = `There was an error (${response.status}) retrieving from the url ` + resultsURL + response.statusText;
        }
        else {
          responseText = await response.text();
          try {
            responseDict = JSON.parse(responseText); 
            responseText = "";
            responseType = "json";
          } catch (e) {
              // this is not a json file
              if (responseText.startsWith("<?xml")) {
                responseType = "xml";
              }
          };

          this.setState({
            responseText: responseText,
            responseDict: responseDict,
            responseType: responseType,
            requestStatus: "",
          }, () => {
              if(this.responseRef.current){
                this.scrollToResponse();
              }
            }
          );   
        }   
      }
    })
    .catch(error => {
      this.setState({ responseText: error.toString() });
      console.error('There was an error!', error);
    });
  }

  /**
   * Loads the a partial amount of the data
   * @param {*} response the response object
   */
  async displayPartialResults(response) {
    const reader = response.body.getReader();
    const contentLength = + response.headers.get('Content-Length');
    let receivedLength = 0; // received that many bytes at the moment
    let chunks = []; // array of received binary chunks (comprises the body)
    while(this.cancelRequested.current === false) {
      const {done, value} = await reader.read();
      if (done || receivedLength > this.MAX_CONTENT_LENGTH) {
        break;
      }

      chunks.push(value);
      receivedLength += value.length;
      console.log(`Received ${receivedLength} of ${contentLength}`)
    }

    let chunksAll = new Uint8Array(receivedLength); // (4.1)
    let position = 0;
    for(let chunk of chunks) {
      chunksAll.set(chunk, position); // (4.2)
      position += chunk.length;
    }

    let result = new TextDecoder("utf-8").decode(chunksAll);

    // handle partial reading of the file
    let responseDict = {};
    let responseText = `Data Product is too large ${contentLength} bytes, roughly ${Math.round(contentLength/70)} lines`;
    let responseType = "unknown";
    responseText = result;
    if (responseText.startsWith("<?xml")) {
      responseType = "xml";
    }
    if(responseText.startsWith("{")) {
      responseType = "json";
      try {
        responseDict = JSON.parse(responseText);
      } catch (e) {
        // this is not a complete json file
        try {
          responseDict = partialParse(responseText)
        } catch (e) {
          // this is json file at all
          responseType = "unknown";
        }
      };
    }

    this.setState({
      responseText: responseText,
      responseDict: responseDict,
      responseType: responseType,
      partialResponse: true,
      requestStatus: "",
    }, () => {
        if(this.responseRef.current){
          this.scrollToResponse();
        }
      }
    );
  }

  /**
   * Extracts the response text formated as requested
   * @returns 
   */
  getResponseText() {
    let data = this.state.responseText;
    if (this.state.indentResponse) {
      if (this.state.responseType === "json") {
        data = JSON.stringify(this.state.responseDict, null, 2);
      }
      else if (this.state.responseType === "xml") {
        data = this.formatXml(data);
      }
    }
    else {
      if (this.state.responseType === "json") {
        data = JSON.stringify(this.state.responseDict);
      }
    }
    return (data);
  }

  loadDataProductLabel() {
    const baseUrl = this.state.url;
    this.setState({ label: {} });
    if (baseUrl !== null) {
      const labelUrl = baseUrl + "label/";
      fetchSigned(labelUrl)
        .then(async response => {
          let responseDict = {};

          if (response !== undefined) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
              responseDict = await response.json();
            } else {
              console.log(response)
            }
          }
          this.setState({ label: responseDict });
        })
        .catch(error => {
          this.setState({ responseText: error.toString() });
          console.error('There was an error!', error);
        });
    }
  }

  /**
   * Renders the response
   * @returns formatted JSX
   */
  renderResponse() {
    const responseText = this.getResponseText();
    if (responseText) {
      return (
        <>
          <div style={{ display: "flex", flexDirection: "row", marginTop: "2em" }} ref={this.responseRef}>
            <OwcTypography style={{ marginTop: "0.1em", flexGrow: "4" }} variant="title5">
              {this.state.partialResponse===true?"Truncated Response (Click Download File for the entire file)":"Response"}
            </OwcTypography>
            <OwcSwitch style={{ marginBottom: "1em" }}
                checked={this.state.indentResponse}
                onValueChange={() => this.setState({ indentResponse: !this.state.indentResponse })}>
              Indented
            </OwcSwitch>
            <OwcButton style={{ marginBottom: "1em" }}
                onClick={() => this.downloadFile()}
                disabled={this.state.resultUrl === ""}>
              Download File
            </OwcButton>
          </div>
          {this.state.responseText === this.REQUEST_IN_PROGRESS ?
            (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <owc-progress-spinner style={{ marginTop: '30px' }} />
              </div>
            ) :
            (
              <div style={{
                    backgroundColor: "#eee", 
                    overflowY: "scroll",
                    overflowX: "scroll",
                    padding: "2em",
                    whiteSpace: "pre-wrap",
                    maxWidth: "90vw",
                    margin: "auto"
                  }}>
                {responseText}
              </div>
            )}
        </>
      );
    }
  }

  /**
  * Renders the request section for the Data Product
  * @returns formatted JSX
  */
  render() {
    if (this.state.selectedDataProduct) {
      let howToQuery = "";
      let description = "";
      let intendedUse = "";
      let dataDictionary = "";
      if (Object.keys(this.state.label).length > 0) {
        const dataProductAttributes = this.state.label[configData.LABEL_KEYS.DATA_PRODUCT_ATTRIBUTES_KEY][configData.LABEL_KEYS.CHILDREN_KEY];
        if (dataProductAttributes !== undefined) {
          if (dataProductAttributes[configData.LABEL_KEYS.DESCRIPTION_KEY] !== undefined) {
            description = dataProductAttributes[configData.LABEL_KEYS.DESCRIPTION_KEY][configData.LABEL_KEYS.VALUE_KEY];
          }
          if (dataProductAttributes[configData.LABEL_KEYS.HOW_TO_QUERY_DATA_KEY] !== undefined) {
            howToQuery = dataProductAttributes[configData.LABEL_KEYS.HOW_TO_QUERY_DATA_KEY][configData.LABEL_KEYS.VALUE_KEY];
          }
          
          if (dataProductAttributes[configData.LABEL_KEYS.INTENDED_USE_KEY] !== undefined) {
            intendedUse = dataProductAttributes[configData.LABEL_KEYS.INTENDED_USE_KEY][configData.LABEL_KEYS.VALUE_KEY];
          }
          
          if (dataProductAttributes[configData.LABEL_KEYS.DATA_DICTIONARY_KEY] !== undefined) {
            dataDictionary = dataProductAttributes[configData.LABEL_KEYS.DATA_DICTIONARY_KEY][configData.LABEL_KEYS.VALUE_KEY];
          }
        }
      }
      return (
        <div style={{ marginTop: "2em", marginLeft: "2em", marginRight: "2em"}}>
          <OwcTypography variant="title5">Request</OwcTypography>
          <div style={{ display: 'flex', flexDirection: "row" }}>
            <OwcTypography style={{ marginTop: "0.75em", marginRight: "1em" }} variant="body">URL</OwcTypography>
            <OwcTypography style={{ marginTop: "0.75em", wordBreak: "break-all"}} variant="body">
              {this.state.url}
            </OwcTypography>
            <div style={{ display: 'flex', flexDirection: "column", flexGrow: "4", marginRight: "1em",}}>
              <div style={{ display: 'flex', flexDirection: "row"}}>
                <OwcInput style={{ flexGrow: "4", alignSelf:"center" }} size="small"
                  value={this.state.params}
                  onValueChange={(ev) => this.handleInputChange(ev.detail)}
                  onKeyDown={(ev) => this.handlekeyDown(ev)}>
                </OwcInput>
              </div>
              <OwcTypography style={{ marginTop: "0.3em", alignSelf: "flex-end" }}
                  variant="title6">
                {this.state.requestStatus}
              </OwcTypography>
            </div>
            <div>
              <OwcButton id="submit" onClick={() => this.handleSubmitClick()}>Sign & Submit</OwcButton>
              <DelayedTooltip anchor="submit" >
                Crytographically signs your request and submits it to the server
              </DelayedTooltip>
              {this.state.requestStatus === ""?
              (
                <>
                <OwcButton id="reset" style={{ marginTop: "0.4em", width:"100%" }}
                    variant="primary"
                    onClick={() => {
                      this.setState({
                        params: null,
                        responseText: "",
                        responseDict: {},
                        partialResponse: false,
                        responseType: "unknown",
                        resultUrl: "",
                      });
                    }}>
                  Reset
                </OwcButton>
                <DelayedTooltip anchor="reset" >
                  Resets the url to the default and removes the current response
                </DelayedTooltip>
                </>
              )
              :
              (
                <>
                <OwcButton id="cancel" style={{ marginTop: "0.4em", width:"100%" }}
                    variant="primary" className="warning-button"
                    onClick={() => {
                      this.cancelRequested.current = true;
                      this.setState({
                        requestStatus: "",
                        responseType: "",
                        responseText: "",
                        resultUrl: "",
                      });
                    }}>
                  Cancel Request
                </OwcButton>
                <DelayedTooltip anchor="cancel" >
                  Cancel the current request
                </DelayedTooltip>
                </>
              )
                }
            </div>
          </div>
          {description !== "" 
            ? (
              <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
                  expanded={this.state.descriptionExpanded}
                  onExpandedChange={(ev) => { this.setState({ descriptionExpanded: ev.detail }) }}>
                <span slot="title">
                  <OwcTypography variant="title6">
                    {configData.LABEL_KEYS.DESCRIPTION_KEY}
                  </OwcTypography>
                </span>
                <span slot="content">
                  <OwcTypography style={{ whiteSpace: "pre-wrap", paddingLeft: "50px" }}>
                    {description}
                  </OwcTypography></span>
              </OwcExpandable>
            )
            : ""
          }
          {howToQuery !== ""
            ? (
              <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
                  expanded={this.state.howToExpanded}
                  onExpandedChange={(ev) => { this.setState({ howToExpanded: ev.detail }) }}>
                <span slot="title">
                  <OwcTypography variant="title6">
                    {configData.LABEL_KEYS.HOW_TO_QUERY_DATA_KEY}
                  </OwcTypography>
                </span>
                <span slot="content">
                  <OwcTypography style={{ whiteSpace: "pre-wrap", paddingLeft: "50px" }}>
                    {howToQuery}
                  </OwcTypography>
                </span>
              </OwcExpandable>
            )
            : ""
          }
          {intendedUse !== "" 
            ? (
              <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
                  expanded={this.state.intendedUseExpanded}
                  onExpandedChange={(ev) => { this.setState({ intendedUseExpanded: ev.detail }) }}>
                <span slot="title">
                  <OwcTypography variant="title6">
                    {configData.LABEL_KEYS.INTENDED_USE_KEY}
                  </OwcTypography>
                </span>
                <span slot="content">
                  <OwcTypography style={{ whiteSpace: "pre-wrap", paddingLeft: "50px"  }}>
                    {intendedUse}
                  </OwcTypography>
                </span>
              </OwcExpandable>
            )
            : ""
          }
          {dataDictionary !== ""
            ? (
              <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
                  expanded={this.state.dataDictionaryExpanded}
                  onExpandedChange={(ev) => { this.setState({ dataDictionaryExpanded: ev.detail }) }}>
                <span slot="title">
                  <OwcTypography variant="title6">
                    {configData.LABEL_KEYS.DATA_DICTIONARY_KEY}
                  </OwcTypography>
                </span>
                <span slot="content">
                  <a href={dataDictionary} 
                      target="_blank" rel="noreferrer noopener" 
                      style={{ paddingLeft: "50px"  }}>
                    {dataDictionary}
                  </a>
                  <OwcTypography style={{ whiteSpace: "pre-wrap", paddingLeft: "50px"  }}>
                    (opens in a new window)
                  </OwcTypography>
                </span>
              </OwcExpandable>
            )
            : ""
          }

          {this.renderResponse()}
        </div>
      );
    }
  }
}

export default GenericDataProduct;
